import { create } from 'zustand';
import { translateApiError } from '@/lib/apiError';
import i18n from '../i18n/config';
import {
  artifactsService,
  type ActiveContext,
  type AppendDocumentRequest,
  type ArtifactDocument,
  type ArtifactMessage,
  type ArtifactSession,
  type ArtifactSessionDetail,
  type ArtifactToolCall,
  type ArtifactUpload,
  type IndexingStatusResponse,
  type RelatedDocument,
  type SaveTarget,
} from '../services/artifactsService';

type AnimationFlags = {
  newIds: Set<string>;
  revIds: Set<string>;
};

interface ArtifactStore {
  sessions: ArtifactSession[];
  loadingSessions: boolean;

  current: ArtifactSessionDetail | null;
  loadingCurrent: boolean;

  // Server-authoritative active domaine/usecase/cadrage for the current session
  // (Workstream A). Mirrors `ArtifactSessionDetail.active_context`. Null when no
  // session is loaded or the session has no active context. Consumed by the Info
  // Sidebar (Workstream B); kept here so the switch flow has a single source of
  // truth. The send-message response does NOT carry the updated context, so it
  // is refreshed from session-detail after any turn that executed a commit tool.
  activeContext: ActiveContext | null;

  expanded: string | null;
  // `animation.newIds`/`revIds` carry the ids of fragments created/revised on the
  // LATEST turn. They serve two purposes on the canvas (Workstream D):
  //   1. The one-shot CSS entry/revision animation (gf-fragment-enter / -rev),
  //      which the browser plays once when the class is freshly applied.
  //   2. A PERSISTENT "recently changed" highlight (req 5 / decision D3 —
  //      canvas only, never exported). Because these sets are only replaced at
  //      turn end (and reset when a new turn begins or the session changes), the
  //      mark survives re-renders after the animation finishes and clears exactly
  //      when the next user turn starts.
  animation: AnimationFlags;

  sending: boolean;
  error: string | null;

  // Save / Add-to-KG state (Workstream C, req 3/4). Shared between the toolbar
  // buttons and the Info Sidebar status slot. `saving` is a dedicated flag so it
  // does not collide with `sending` (chat turn in flight). `savedDocument` /
  // `saveTarget` describe the last successful save for the CURRENT session;
  // `indexingStatus` tracks live indexing progress while polling. All reset on
  // session change so a stale save never leaks across sessions.
  saving: boolean;
  savedDocument: RelatedDocument | null;
  saveTarget: SaveTarget | null;
  indexingStatus: IndexingStatusResponse['status'] | null;
  saveError: string | null;

  // Cross-page hand-off: message queued by the Welcome page, consumed by
  // GenieArtifactPage once the matching session is loaded. Survives navigation
  // because it lives in the store (React Router state can be lost on refresh
  // or replace navigation).
  pendingMessage: { sessionId: string; content: string } | null;

  loadSessions: () => Promise<void>;
  selectSession: (sessionId: string | null) => Promise<void>;
  // `projectId` starts the session from a project (L2d): the server sets the
  // project as active context and writes the welcome message itself.
  createSession: (title?: string | null, projectId?: string | null) => Promise<ArtifactSession>;
  deleteSession: (sessionId: string) => Promise<void>;
  renameSession: (sessionId: string, title: string | null) => Promise<void>;

  appendDocument: (sessionId: string, payload: AppendDocumentRequest) => Promise<ArtifactDocument>;
  uploadFile: (sessionId: string, file: File) => Promise<ArtifactUpload>;
  deleteUpload: (sessionId: string, uploadId: string) => Promise<void>;

  sendMessage: (sessionId: string, content: string) => Promise<void>;

  saveArtifactDocument: (index: boolean) => Promise<void>;

  setExpanded: (id: string | null) => void;
  setPendingMessage: (entry: { sessionId: string; content: string } | null) => void;
}

const emptyAnim: AnimationFlags = { newIds: new Set(), revIds: new Set() };

// Reset shape for the Workstream C save/index state. Applied on every session
// change so a save surfaced for one session never bleeds into the next.
const emptySaveState = {
  saving: false,
  savedDocument: null,
  saveTarget: null,
  indexingStatus: null,
  saveError: null,
} as const;

// Indexing status polling cadence and cap (Workstream C). We poll every ~2s and
// give up after ~60s so a stuck backend job doesn't spin forever.
const INDEXING_POLL_INTERVAL_MS = 2000;
const INDEXING_POLL_MAX_MS = 60_000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Normalize an assistant/user message coming off the wire so `questions` is
// always a concrete array (Workstream E). The backend defaults to `[]`, but
// older cached payloads may omit the field entirely; downstream rendering gates
// on `questions.length`, so a guaranteed array keeps that check total.
function withQuestions(message: ArtifactMessage): ArtifactMessage {
  return { ...message, questions: message.questions ?? [] };
}

// A commit tool is the second, confirm-gated phase of any write (create_*,
// update_*, promote_*, set_active_context, …). Their results mutate
// server-side state that the send-message response does not echo back —
// notably the session's active context — so we detect them to trigger a
// targeted refresh. Preview/read tools never mutate that state.
function executedCommitTool(calls: ArtifactToolCall[] | null | undefined): boolean {
  if (!calls) return false;
  return calls.some((c) => {
    if (typeof c.name !== 'string') return false;
    if (c.name.startsWith('preview_')) return false;
    return (
      c.name.startsWith('create_') ||
      c.name.startsWith('update_') ||
      c.name.startsWith('promote_') ||
      c.name === 'set_active_context'
    );
  });
}

export const useArtifactStore = create<ArtifactStore>((set, get) => ({
  sessions: [],
  loadingSessions: false,
  current: null,
  loadingCurrent: false,
  activeContext: null,
  expanded: null,
  animation: emptyAnim,
  sending: false,
  error: null,

  ...emptySaveState,

  pendingMessage: null,

  setExpanded: (id) => set({ expanded: id }),
  setPendingMessage: (entry) => set({ pendingMessage: entry }),

  loadSessions: async () => {
    set({ loadingSessions: true, error: null });
    try {
      const sessions = await artifactsService.listSessions(0, 100);
      set({ sessions, loadingSessions: false });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : i18n.t('genieArtifact.errors.loadSessions');
      set({ loadingSessions: false, error: msg });
    }
  },

  selectSession: async (sessionId) => {
    if (!sessionId) {
      set({
        current: null,
        activeContext: null,
        animation: emptyAnim,
        expanded: null,
        ...emptySaveState,
      });
      return;
    }
    set({
      loadingCurrent: true,
      error: null,
      animation: emptyAnim,
      activeContext: null,
      ...emptySaveState,
    });
    try {
      const detail = await artifactsService.getSession(sessionId);
      set((state) => {
        // Race guard (BUG 3): between kicking off this fetch and its resolution,
        // the Welcome hand-off (pendingMessage) may have already run
        // `sendMessage` for THIS session and optimistically appended the user's
        // first message — or a StrictMode double-mount may have fired a second,
        // later-resolving load. A naive overwrite with the freshly-fetched
        // (still message-less) detail would clobber that optimistic bubble,
        // leaving only the "Rédaction de la réponse" spinner with no user turn.
        //
        // So when the store already holds the SAME session, preserve any local
        // messages the server detail doesn't yet include (matched by id — temp
        // ids and just-sent messages the fetch predates) by appending them after
        // the server's authoritative list.
        const serverMessages = detail.messages.map(withQuestions);
        const localMessages =
          state.current?.id === sessionId ? state.current.messages : [];
        const serverIds = new Set(serverMessages.map((m) => m.id));
        const preserved = localMessages.filter((m) => !serverIds.has(m.id));

        return {
          current: {
            ...detail,
            messages: [...serverMessages, ...preserved],
          },
          activeContext: detail.active_context,
          loadingCurrent: false,
          expanded: null,
        };
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : i18n.t('genieArtifact.errors.loadSession');
      set({ loadingCurrent: false, error: msg });
    }
  },

  createSession: async (title = null, projectId = null) => {
    const newSession = await artifactsService.createSession(title, projectId);
    set((state) => ({ sessions: [newSession, ...state.sessions] }));
    return newSession;
  },

  deleteSession: async (sessionId) => {
    await artifactsService.deleteSession(sessionId);
    set((state) => ({
      sessions: state.sessions.filter((s) => s.id !== sessionId),
      current: state.current?.id === sessionId ? null : state.current,
      activeContext: state.current?.id === sessionId ? null : state.activeContext,
      ...(state.current?.id === sessionId ? emptySaveState : {}),
    }));
  },

  renameSession: async (sessionId, title) => {
    const updated = await artifactsService.updateSession(sessionId, title);
    set((state) => ({
      sessions: state.sessions.map((s) => (s.id === sessionId ? { ...s, title: updated.title } : s)),
      current:
        state.current?.id === sessionId
          ? { ...state.current, title: updated.title }
          : state.current,
    }));
  },

  appendDocument: async (sessionId, payload) => {
    const beforeIds = new Set(
      get().current?.current_document?.fragments.map((f) => f.id) ?? []
    );
    const doc = await artifactsService.appendDocument(sessionId, payload);
    const concepts = await artifactsService.listConcepts(sessionId);

    const newIds = new Set<string>();
    const revIds = new Set<string>();
    for (const f of doc.fragments) {
      if (!beforeIds.has(f.id)) newIds.add(f.id);
      if (f.replaces && beforeIds.has(f.replaces)) revIds.add(f.id);
    }

    set((state) => ({
      current: state.current
        ? { ...state.current, current_document: doc, concepts }
        : state.current,
      animation: { newIds, revIds },
    }));
    return doc;
  },

  uploadFile: async (sessionId, file) => {
    const upload = await artifactsService.uploadFile(sessionId, file);
    set((state) => ({
      current: state.current
        ? { ...state.current, uploads: [...state.current.uploads, upload] }
        : state.current,
    }));
    return upload;
  },

  deleteUpload: async (sessionId, uploadId) => {
    await artifactsService.deleteUpload(sessionId, uploadId);
    set((state) => ({
      current: state.current
        ? {
            ...state.current,
            uploads: state.current.uploads.filter((u) => u.id !== uploadId),
          }
        : state.current,
    }));
  },

  sendMessage: async (sessionId, content) => {
    if (!content.trim()) return;
    // Clear the previous turn's "recently changed" marks the instant a new turn
    // begins. `animation.newIds`/`revIds` drive both the one-shot entry animation
    // and the persistent canvas-only change highlight (Workstream D, req 5 /
    // decision D3). Resetting here is what makes the highlight persist across
    // re-renders during a turn yet disappear the moment the user sends the next
    // message; it is re-populated at turn end below.
    set({ sending: true, error: null, animation: emptyAnim });
    const beforeIds = new Set(
      get().current?.current_document?.fragments.map((f) => f.id) ?? []
    );
    // Optimistic: append a temporary user message immediately
    const tempUserMsg: ArtifactMessage = {
      id: `temp-${Date.now()}`,
      is_user: true,
      content,
      narration: null,
      tool_calls: null,
      coverage_pct: null,
      created_at: new Date().toISOString(),
      questions: [],
    };
    // Append the optimistic bubble only when it belongs to the currently-loaded
    // session. Guarding on the session id keeps the Welcome hand-off race-safe:
    // if a session switch is mid-flight the append (and the eventual reply
    // merge) targets the right thread rather than leaking into another session.
    set((state) =>
      state.current?.id === sessionId
        ? {
            current: {
              ...state.current,
              messages: [...state.current.messages, tempUserMsg],
            },
          }
        : {}
    );
    try {
      const resp = await artifactsService.sendMessage(sessionId, content);

      const newIds = new Set<string>();
      const revIds = new Set<string>();
      if (resp.document) {
        for (const f of resp.document.fragments) {
          if (!beforeIds.has(f.id)) newIds.add(f.id);
          if (f.replaces && beforeIds.has(f.replaces)) revIds.add(f.id);
        }
      }

      set((state) => {
        // Only fold the reply into the thread it belongs to. A session switch
        // that landed while the request was in flight leaves `current` pointing
        // elsewhere; in that case drop the result rather than corrupt the now-
        // visible session.
        if (!state.current || state.current.id !== sessionId) {
          return { sending: false };
        }
        // Remove the temp message and append the real ones. Normalize so each
        // carries a concrete `questions` array (Workstream E) — the assistant
        // message's questions drive the clarifying-question panel in the chat.
        const messages = state.current.messages.filter(
          (m) => m.id !== tempUserMsg.id
        );
        messages.push(
          withQuestions(resp.user_message),
          withQuestions(resp.assistant_message)
        );

        // The orchestrator may have set the session title (LLM-supplied) on
        // this turn — mirror it locally so the sidebar reflects the change
        // without a refetch.
        const updatedTitle = resp.session?.title ?? state.current.title;
        const sessions = resp.session
          ? state.sessions.map((s) =>
              s.id === resp.session!.id ? { ...s, title: resp.session!.title, updated_at: resp.session!.updated_at } : s
            )
          : state.sessions;

        return {
          current: {
            ...state.current,
            title: updatedTitle ?? null,
            messages,
            current_document: resp.document ?? state.current.current_document,
            concepts: resp.concepts,
          },
          sessions,
          animation: { newIds, revIds },
          sending: false,
        };
      });

      // The send-message response does not echo the (possibly switched) active
      // context — only session-detail does. After any turn that executed a
      // commit tool (e.g. a confirmed set_active_context), re-fetch the detail
      // and refresh `activeContext`. Targeted: we update only the active context
      // so the message list rendered above is left untouched. Guard against a
      // session switch racing the fetch by re-checking the current session id.
      if (executedCommitTool(resp.assistant_message.tool_calls)) {
        try {
          const detail = await artifactsService.getSession(sessionId);
          if (get().current?.id === sessionId) {
            set({ activeContext: detail.active_context });
          }
        } catch {
          // A failed refresh leaves the prior active context in place; the next
          // session-detail load reconciles it. Not worth surfacing an error.
        }
      }
    } catch (e: unknown) {
      const msg = translateApiError(e, i18n.t('genieArtifact.errors.sendFailed'));
      set((state) => ({
        sending: false,
        error: msg,
        current:
          state.current?.id === sessionId
            ? {
                ...state.current,
                messages: state.current.messages.filter((m) => m.id !== tempUserMsg.id),
              }
            : state.current,
      }));
    }
  },

  // Save the current document to the KB (Workstream C). `index: true` = "Add to
  // KG" (save + queue indexing then poll live status); `index: false` = "Save as
  // doc" (save only). All polling is guarded on the session id captured at call
  // time so a session switch mid-flight can never write stale status.
  saveArtifactDocument: async (index) => {
    const session = get().current;
    if (!session) return;
    const sessionId = session.id;
    if (get().saving) return;

    set({ saving: true, saveError: null });
    try {
      const resp = await artifactsService.saveDocument(sessionId, index);
      // Bail if the session changed while the save was in flight.
      if (get().current?.id !== sessionId) return;

      set({
        savedDocument: resp.document,
        saveTarget: resp.target,
        indexingStatus: resp.document.indexing_status,
        saving: false,
      });

      // Only an indexing save (index:true and the backend actually queued a job)
      // has a live status worth polling.
      if (!index && !resp.indexing_triggered) return;
      if (!resp.indexing_triggered) return;

      const documentId = resp.document.id;
      const deadline = Date.now() + INDEXING_POLL_MAX_MS;
      // Poll until the job settles (indexed/failed), the cap elapses, or the
      // session changes underneath us — whichever comes first.
      while (Date.now() < deadline) {
        await sleep(INDEXING_POLL_INTERVAL_MS);
        if (get().current?.id !== sessionId) return;
        try {
          const statusResp = await artifactsService.getIndexingStatus(documentId);
          if (get().current?.id !== sessionId) return;
          set((state) => ({
            indexingStatus: statusResp.status,
            savedDocument:
              state.savedDocument && state.savedDocument.id === documentId
                ? { ...state.savedDocument, indexing_status: statusResp.status }
                : state.savedDocument,
          }));
          if (statusResp.status === 'indexed' || statusResp.status === 'failed') {
            return;
          }
        } catch {
          // Transient poll error: keep the last known status and retry on the
          // next tick rather than aborting the whole poll loop.
        }
      }
    } catch (e: unknown) {
      if (get().current?.id !== sessionId) return;
      const msg = e instanceof Error ? e.message : i18n.t('genieArtifact.errors.saveFailed');
      set({ saving: false, saveError: msg });
    }
  },
}));
